import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState
} from 'react';

import { useDropdownPortal } from '../../hooks/useDropdownPortal';
import { Sort } from '../../enums/Sort';
import ChevronDown from '../Icons/Chevron/ChevronDown';

/**
 * Supported input types for the dropdown.
 *  - 'date': only date (YYYY-MM-DD)
 *  - 'datetime': date + time (YYYY-MM-DDTHH:mm)
 */
type Mode = 'date' | 'datetime';

/**
 * Type representing a selected date range + sort direction.
 */
export type DateRange = { start?: string; end?: string; sort?: Sort };

/**
 * Public handle for clearing this component externally via ref.
 */
export type DateRangeDropdownHandle = {
    clear: () => void;
};

/**
 * Pads single-digit numbers to two digits.
 *
 * @param n - The number to pad.
 * @returns The padded string representation (e.g., `5 → '05'`).
 */
const pad = (n: number) => (n < 10 ? `0${n}` : String(n));

/**
 * Converts an ISO string into an `<input>` value suitable for `date` or `datetime-local`.
 *
 * @param iso - The ISO date string.
 * @param mode - The mode determining whether to extract date or datetime.
 * @returns A formatted input value string.
 */
const toInputValue = (iso?: string, mode: Mode = 'date'): string => {
    if (!iso) return '';
    if (mode === 'date') return iso.slice(0, 10);
    const d = new Date(iso);
    if (!isNaN(d.getTime())) {
        const y = d.getFullYear();
        const m = pad(d.getMonth() + 1);
        const day = pad(d.getDate());
        const hr = pad(d.getHours());
        const min = pad(d.getMinutes());
        return `${y}-${m}-${day}T${hr}:${min}`;
    }
    return iso.length >= 16 ? iso.slice(0, 16) : '';
};

/**
 * Converts a user-entered date/datetime input value back into ISO format.
 *
 * @param input - The user-entered date or datetime string.
 * @param mode - The mode determining whether the input is `date` or `datetime-local`.
 * @param opts - Optional settings for conversion.
 * @param opts.utc - Whether to convert to UTC (default: `true`).
 * @param opts.includeSeconds - Whether to include seconds in the result (default: `true`).
 * @returns The ISO-formatted string or `undefined` if invalid.
 */
const fromInputValue = (
    input: string,
    mode: Mode,
    opts?: { utc?: boolean; includeSeconds?: boolean }
): string | undefined => {
    if (!input) return undefined;
    if (mode === 'date') return input;

    const { utc = true, includeSeconds = true } = opts || {};
    if (!utc) return includeSeconds ? `${input}:00` : input;

    const [datePart, timePart] = input.split('T');
    const [yy, mm, dd] = datePart.split('-').map(Number);
    const [hh, mi] = timePart.split(':').map(Number);
    const dt = new Date(yy, (mm ?? 1) - 1, dd ?? 1, hh ?? 0, mi ?? 0, 0);
    const iso = dt.toISOString();
    return includeSeconds ? iso.replace(/\.\d{3}Z$/, 'Z') : iso.slice(0, 16);
};

/**
 * Formats start/end ISO dates for display in the dropdown button.
 *
 * @param start - The ISO string for the start date.
 * @param end - The ISO string for the end date.
 * @param mode - The display mode (`'date'` or `'datetime'`).
 * @returns A formatted string like `'2025-10-01 → 2025-10-15'`.
 */
const formatDisplay = (start?: string, end?: string, mode: Mode = 'date'): string => {
    if (!start && !end) return '';
    if (mode === 'date') {
        const s = start ? start.slice(0, 10) : '';
        const e = end ? end.slice(0, 10) : '';
        return `${s || '…'} → ${e || '…'}`;
    }
    const fmt = (iso?: string) => {
        if (!iso) return '…';
        const d = new Date(iso);
        if (isNaN(d.getTime())) return iso.slice(0, 16).replace('T', ' ');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    return `${fmt(start)} → ${fmt(end)}`;
};

/**
 * Ensures the date range is in chronological order.
 *
 * @param s - The start date string.
 * @param e - The end date string.
 * @returns A normalized object where start ≤ end.
 */
const normalizeRange = (s?: string, e?: string) => {
    if (s && e && s > e) return { start: e, end: s };
    return { start: s, end: e };
};

/**
 * Props for the DateRangeDropdown component.
 */
type DateRangeDropdownProps = {
    label?: string;
    mode?: Mode;
    utc?: boolean;
    includeSeconds?: boolean;
    defaultStart?: string;
    defaultEnd?: string;
    onChange?: (range: DateRange) => void;
    placeholder?: string;
    clearable?: boolean;
    size?: 'sm' | 'lg';
    disabled?: boolean;
    wrapperClassName?: string;
    triggerClassName?: string;
    buttonWidthCh?: number;
    sortable?: boolean;

    /** Optional controlled sort value from the parent */
    sort?: Sort | undefined;
};

/**
 * A date range picker component with optional sort toggle (Ascending/Descending/None).
 *
 * @param props - Component props.
 * @param props.label - Optional label displayed above the dropdown.
 * @param props.mode - Input mode: `'date'` or `'datetime'`.
 * @param props.utc - Whether to convert inputs to UTC (default `true`).
 * @param props.includeSeconds - Whether to include seconds (default `true`).
 * @param props.defaultStart - Default start ISO date string.
 * @param props.defaultEnd - Default end ISO date string.
 * @param props.onChange - Callback fired when the range or sort changes.
 * @param props.placeholder - Text shown when no range is selected.
 * @param props.clearable - Whether the range can be cleared.
 * @param props.size - Button size (`'sm'` or `'lg'`).
 * @param props.disabled - Whether inputs are disabled.
 * @param props.wrapperClassName - Optional extra classes for the wrapper.
 * @param props.triggerClassName - Optional extra classes for the trigger button.
 * @param props.buttonWidthCh - Maximum button width in `ch`.
 * @param props.sortable - Whether sorting (Asc/Desc/None) is enabled.
 * @param props.sort - Controlled sort value from the parent.
 * @param ref - A ref exposing a `.clear()` method to reset the component.
 * @returns The rendered `DateRangeDropdown` React component.
 */
export const DateRangeDropdown = forwardRef<DateRangeDropdownHandle, DateRangeDropdownProps>(({
    label,
    mode = 'date',
    utc = true,
    includeSeconds = true,
    defaultStart,
    defaultEnd,
    onChange,
    placeholder = 'Select dates…',
    clearable = true,
    size,
    disabled,
    wrapperClassName,
    triggerClassName,
    buttonWidthCh,
    sortable = false,
    sort: externalSort,
}, ref) => {
    /** --- State --- */
    const [open, setOpen] = useState(false);
    const [start, setStart] = useState<string | undefined>(defaultStart);
    const [end, setEnd] = useState<string | undefined>(defaultEnd);
    const [sort, setSort] = useState<Sort | undefined>(sortable ? Sort.Ascending : undefined);

    /** --- Controlled Sort Synchronization --- */
    useEffect(() => {
        if (sortable && externalSort !== undefined && externalSort !== sort) {
            setSort(externalSort);
        }
        if (sortable && externalSort === undefined && sort !== undefined) {
            setSort(undefined);
        }
    }, [externalSort, sortable, sort]);

    /** --- Emit initial Ascending sort once --- */
    const didEmitInitial = useRef(false);
    useEffect(() => {
        if (!sortable || didEmitInitial.current) return;
        didEmitInitial.current = true;
        emit(start, end, Sort.Ascending);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /** --- Setup DOM Refs + Dropdown Portal --- */
    const containerRef = useRef<HTMLDivElement | null>(null);
    const type = mode === 'date' ? 'date' : 'datetime-local';
    const sizeBtn = size === 'sm' ? 'btn-sm' : size === 'lg' ? 'btn-lg' : '';
    const widthCh = buttonWidthCh ?? (mode === 'date' ? 20 : 40);
    const { portal } = useDropdownPortal(containerRef, open, setOpen);

    /**
     * Emits the current range and sort state via `onChange`.
     *
     * @param s - The start date string.
     * @param e - The end date string.
     * @param currentSort - The sort order (Ascending, Descending, or None).
     */
    const emit = useCallback((s?: string, e?: string, currentSort?: Sort) => {
        onChange?.({ start: s, end: e, sort: currentSort });
    }, [onChange]);

    /**
     * Clears the start, end, and sort values.
     */
    const handleClear = useCallback(() => {
        setStart(undefined);
        setEnd(undefined);
        setSort(undefined);
        emit(undefined, undefined, undefined);
    }, [emit]);

    useImperativeHandle(ref, () => ({ clear: handleClear }), [handleClear]);

    /**
     * Handles changes to the start input field.
     *
     * @param raw - The raw input string from the user.
     */
    const handleStart = (raw: string) => {
        const iso = fromInputValue(raw, mode, { utc, includeSeconds });
        const { start: s, end: e } = normalizeRange(iso, end);
        setStart(s);
        setEnd(e);
        emit(s, e, sort);
    };

    /**
     * Handles changes to the end input field.
     *
     * @param raw - The raw input string from the user.
     */
    const handleEnd = (raw: string) => {
        const iso = fromInputValue(raw, mode, { utc, includeSeconds });
        const { start: s, end: e } = normalizeRange(start, iso);
        setStart(s);
        setEnd(e);
        emit(s, e, sort);
    };

    /**
     * Cycles the sort direction: Asc → Desc → None → Asc.
     */
    const cycleSort = () => {
        if (!sortable || disabled) return;
        const next =
            sort === Sort.Ascending
                ? Sort.Descending
                : sort === Sort.Descending
                ? undefined
                : Sort.Ascending;
        setSort(next);
        emit(start, end, next);
    };

    /**
     * Handles keyboard events for the label to toggle sorting.
     *
     * @param e - The keyboard event.
     */
    const onLabelKeyDown: React.KeyboardEventHandler<HTMLLabelElement> = (e) => {
        if (!sortable || disabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            cycleSort();
        }
    };

    /**
     * Handles keyboard navigation for the dropdown button.
     *
     * @param e - The keyboard event.
     */
    const onTriggerKeyDown: React.KeyboardEventHandler<HTMLButtonElement> = (e) => {
        if (disabled) return;
        if (e.key === ' ' || e.key === 'Enter' || e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(v => !v);
        } else if (e.key === 'Escape') {
            setOpen(false);
        }
    };

    const display = formatDisplay(start, end, mode);
    const sortGlyph = !sortable
        ? ''
        : sort === undefined
        ? '⮃'
        : sort === Sort.Ascending
        ? '⭡'
        : '⭣';

    return (
        <div
            className={['position-relative', wrapperClassName].filter(Boolean).join(' ')}
            ref={containerRef}
        >
            {/* Label */}
            {label && (
                <label
                    className='form-label fw-semibold d-block mb-1'
                    onClick={cycleSort}
                    onKeyDown={onLabelKeyDown}
                    tabIndex={sortable && !disabled ? 0 : undefined}
                    style={sortable ? { cursor: 'pointer' } : undefined}
                    aria-disabled={disabled || !sortable || undefined}
                    aria-label={
                        sortable
                            ? `${label} sort ${
                                  sort === undefined
                                      ? 'none'
                                      : sort === Sort.Ascending
                                      ? 'ascending'
                                      : 'descending'
                              }`
                            : undefined
                    }
                >
                    {label}
                    {sortable && <span aria-hidden='true'> {sortGlyph}</span>}
                </label>
            )}

            {/* Main button trigger */}
            <button
                type='button'
                className={[
                    'btn',
                    'btn-outline-secondary',
                    'btn-form-select',
                    'btn-form-focus',
                    'd-flex',
                    'align-items-center',
                    'justify-content-between',
                    'gap-2',
                    'text-truncate',
                    sizeBtn,
                    triggerClassName
                ].filter(Boolean).join(' ')}
                style={{
                    width: '100%',
                    ...(widthCh ? { maxWidth: `${widthCh}ch` } : {})
                }}
                onClick={() => !disabled && setOpen(v => !v)}
                onKeyDown={onTriggerKeyDown}
                aria-expanded={open}
                aria-haspopup='dialog'
                disabled={disabled}
            >
                <span className='flex-grow-1 text-truncate text-start text-muted min-w-0'>
                    {display || placeholder}
                </span>
                <ChevronDown />
            </button>

            {/* Dropdown menu */}
            {portal(
                <div
                    className='dropdown-menu show p-3 shadow rounded-3 overflow-hidden enum-menu'
                    role='dialog'
                    aria-label='Choose date range'
                    style={{
                        width: '100%',
                        ...(widthCh ? { maxWidth: `${widthCh}ch` } : {})
                    }}
                >
                    {/* Start date input */}
                    <div className='mb-2'>
                        <label className='form-label mb-1'>Start</label>
                        <input
                            type={type}
                            className='form-control'
                            value={toInputValue(start, mode)}
                            onChange={(e) => handleStart(e.target.value)}
                            disabled={disabled}
                        />
                    </div>

                    {/* End date input */}
                    <div className='mb-3'>
                        <label className='form-label mb-1'>End</label>
                        <input
                            type={type}
                            className='form-control'
                            value={toInputValue(end, mode)}
                            onChange={(e) => handleEnd(e.target.value)}
                            disabled={disabled}
                        />
                    </div>

                    {/* Footer buttons */}
                    <div className='d-flex gap-2 justify-content-end'>
                        {clearable && (
                            <button
                                type='button'
                                className='btn btn-outline-secondary'
                                onClick={handleClear}
                                disabled={disabled}
                            >
                                Clear
                            </button>
                        )}
                        <button
                            type='button'
                            className='btn btn-primary'
                            onClick={() => setOpen(false)}
                            disabled={disabled}
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
});

DateRangeDropdown.displayName = 'DateRangeDropdown';
export default DateRangeDropdown;
